import type { BracketData, BracketFormat, BracketMatch, BracketTeam } from "@/entities/bracket/model/types";

type ArchiveSnapshotDto = {
  snapshotVersion: number;
  bracketType: string;
  matchesSnapshot: string;
};

type TeamArchiveDto = {
  originalTeamId: string;
  name: string;
  icon?: string | null;
};

type ArchivedMatch = {
  id: string;
  round: number;
  matchNumber: number;
  position?: number | null;
  bracketSection?: string | null;
  sectionRound?: number | null;
  nextMatchIds?: string[] | null;
  participant1Id?: string | null;
  participant2Id?: string | null;
  team1Id?: string | null;
  team2Id?: string | null;
  draftTeam1Id?: string | null;
  draftTeam2Id?: string | null;
  team1Score?: number | null;
  team2Score?: number | null;
  status?: string | null;
  /** Формат серии. Без него архив рисовал BO1 даже у Bo3-гранд-финала. */
  bestOf?: number | null;
};

const bracketTypeToFormat = (raw: string): BracketFormat => {
  const s = (raw ?? "").toLowerCase();
  if (s.includes("double")) return "double_elimination";
  if (s.includes("swiss")) return "swiss";
  if (s.includes("roundrobin") || s.includes("round_robin")) return "round_robin";
  return "single_elimination";
};

export function buildBracketDataFromArchive(
  tournamentId: string,
  snapshot: ArchiveSnapshotDto,
  teams: TeamArchiveDto[],
): BracketData | null {
  if (!snapshot || (snapshot.snapshotVersion !== 1 && snapshot.snapshotVersion !== 2))
    return null;

  let parsed: ArchivedMatch[] = [];
  try {
    parsed = JSON.parse(snapshot.matchesSnapshot || "[]") as ArchivedMatch[];
  } catch {
    parsed = [];
  }

  const format = bracketTypeToFormat(snapshot.bracketType);

  const bracketTeams: BracketTeam[] = teams.map((t) => ({
    id: t.originalTeamId,
    name: t.name,
    ...(t.icon ? { avatar: t.icon } : {}),
  }));

  const matches: BracketMatch[] = parsed.map((m) => {
    const aId = m.participant1Id ?? m.team1Id ?? m.draftTeam1Id ?? undefined;
    const bId = m.participant2Id ?? m.team2Id ?? m.draftTeam2Id ?? undefined;

    const scoreA = typeof m.team1Score === "number" ? m.team1Score : undefined;
    const scoreB = typeof m.team2Score === "number" ? m.team2Score : undefined;

    const normalizedBracketSection = (m.bracketSection ?? "").toLowerCase();
    const bracket =
      normalizedBracketSection === "winners" ||
      normalizedBracketSection === "losers" ||
      normalizedBracketSection === "finals"
        ? (normalizedBracketSection as "winners" | "losers" | "finals")
        : format === "double_elimination"
          ? m.position === 9999
            ? "finals"
            : typeof m.position === "number" && m.position >= 1000
              ? "losers"
              : "winners"
          : undefined;

    const roundIndex =
      typeof m.sectionRound === "number" && m.sectionRound > 0
        ? m.sectionRound - 1
        : Math.max(0, (m.round ?? 1) - 1);

    const nextMatchIds =
      Array.isArray(m.nextMatchIds) && m.nextMatchIds.length > 0
        ? m.nextMatchIds
        : undefined;

    return {
      id: m.id,
      matchNumber: m.matchNumber,
      roundIndex,
      ...(bracket ? { bracket } : {}),
      sides: [
        { teamId: aId, ...(typeof scoreA === "number" ? { score: scoreA } : {}) },
        { teamId: bId, ...(typeof scoreB === "number" ? { score: scoreB } : {}) },
      ],
      status: m.status ?? undefined,
      // Формат серии берём из снимка. Раньше его там не было, и сетка архива
      // подписывала BO1 всем матчам подряд, включая Bo3-гранд-финал со счётом 2:0.
      ...(m.bestOf === 1 || m.bestOf === 3 || m.bestOf === 5 ? { bestOf: m.bestOf } : {}),
      ...(nextMatchIds ? { nextMatchIds } : {}),
    };
  });

  // Для elimination попробуем восстановить nextMatchIds по round/matchNumber (опционально).
  if (format === "single_elimination" && snapshot.snapshotVersion === 1) {
    const byRoundAndNum = new Map<string, string>();
    for (const m of parsed) {
      const key = `${m.round}:${m.matchNumber}`;
      byRoundAndNum.set(key, m.id);
    }

    matches.forEach((bm) => {
      const round = (bm.roundIndex ?? 0) + 1;
      const num = bm.matchNumber ?? 0;
      if (!round || !num) return;
      const nextRound = round + 1;
      const nextMatchNumber = Math.floor((num + 1) / 2);
      const nextId = byRoundAndNum.get(`${nextRound}:${nextMatchNumber}`);
      if (nextId) bm.nextMatchIds = [nextId];
    });
  }

  if (format === "double_elimination" && snapshot.snapshotVersion === 1) {
    // Восстанавливаем связи отдельно внутри winners/losers. Finals оставляем без ребер.
    const byRoundPos = new Map<string, string>();
    for (const m of parsed) {
      if (typeof m.position !== "number") continue;
      const key = `${m.round}:${m.position}`;
      byRoundPos.set(key, m.id);
    }

    const matchById = new Map<string, BracketMatch>();
    for (const bm of matches) matchById.set(bm.id, bm);

    for (const m of parsed) {
      if (typeof m.position !== "number") continue;
      if (m.position === 9999) continue;

      const bm = matchById.get(m.id);
      if (!bm) continue;

      const isWinners = m.position < 1000;
      const isLosers = m.position >= 1000;

      if (isWinners) {
        const nextRound = m.round + 1;
        const localPos = m.round === 1 ? m.position : m.position - (m.round - 1) * 100;
        const nextPos = m.round * 100 + Math.floor(localPos / 2);
        const nextId = byRoundPos.get(`${nextRound}:${nextPos}`);
        if (nextId) bm.nextMatchIds = [nextId];
      } else if (isLosers) {
        const nextRound = m.round + 1;
        const nextId = byRoundPos.get(`${nextRound}:${m.position}`);
        if (nextId) bm.nextMatchIds = [nextId];
      }
    }
  }

  return {
    id: `archive:${tournamentId}`,
    format,
    teams: bracketTeams,
    matches,
  };
}

